import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  V2Card,
  V2CardHeader,
  V2FormGrid,
  V2FormField,
  V2FormActions,
  V2Button,
  Alert,
  LoadingState,
  StatePanel,
} from '@sakhtar/design-system/react';
import { systemClock } from '@sakhtar/calendar';
import { apiGet, apiPatch, apiPost, ApiError } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { findEntityConfig } from '../../lib/masterDataConfig.js';
import { masterDataFormValue } from '../../lib/masterDataValues.js';
import { masterDataSubmitErrorMessage } from '../../lib/masterDataErrors.js';
import { fetchNextCode } from '../../lib/suggestedCode.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import { EntityPickerField } from './EntityPickerField.js';
import {
  PriceListLinesEditor,
  draftsFromListLines,
  priceLinesError,
  priceLinesPayload,
  type PriceLineDraft,
} from './PriceListLinesEditor.js';
import { ProductCategoryField } from './ProductCategoryField.js';
import {
  EMPTY_UNITS_DRAFT,
  ProductUnitsEditor,
  draftFromCatalogUnits,
  unitsDraftError,
  unitsPayload,
  type UnitsDraft,
} from './ProductUnitsEditor.js';
import type { CatalogProduct, MasterDataRecord } from '../../lib/types/masterData.js';
import { useTenantSettings } from '../../lib/tenantSettings.js';
import { defaultPartyKind } from '../../lib/types/tenantSettings.js';

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Pilot P0 §5.3 اطلاعات پایه — form pattern (shared across entities)
 * canonical pattern: V2Card + V2FormGrid/V2FormField/V2FormActions — the
 *   same real form convention every other real V2 create/edit form in this
 *   app already uses (OpenItemsPage, TreasuryPage, TeamStep, …), on the V2
 *   (`sk-*`) system this master-data trio was still missing.
 * state reference: default · validation error · duplicate/conflict
 *
 * One generic form for all 5 entities, driven by masterDataConfig.ts's
 * per-entity FieldConfig list — matches the identical create/PATCH shape
 * every apps/api/src/master-data/*.controller.ts exposes.
 *
 * Required-field marking is verified against
 * `packages/foundation/src/types.ts`'s `Create*Input` shapes (code/name/
 * kind/baseUomId/rateNumerator/rateDenominator required, `status?` always
 * optional with a `'active'` server-side default) — `status`'s `required`
 * flag was fixed to `false` in `masterDataConfig.ts` to match; this form
 * seeds the same `'active'` default for create so the now-optional select
 * never posts an empty string.
 */
/** `multi-select` fields (e.g. party.roles) live in the generic string-keyed
 * form state as a comma-joined string, converted to/from a real array only
 * at the record load / submit boundary. */
function multiSelectValues(raw: string | undefined): readonly string[] {
  return raw ? raw.split(',').filter((v) => v !== '') : [];
}

function toggleMultiSelectValue(raw: string | undefined, value: string): string {
  const current = multiSelectValues(raw);
  const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
  return next.join(',');
}

function fieldRequired(
  field: { required?: boolean; autoCode?: boolean },
  isEdit: boolean,
  suggested = false,
): boolean {
  return (field.required ?? false) || (field.autoCode === true && isEdit) || suggested;
}

export function EntityFormPage() {
  const { slug, entity, id } = useParams<{ slug: string; entity: string; id?: string }>();
  const config = findEntityConfig(entity);
  const navigate = useNavigate();
  const isEdit = id !== undefined;
  const settings = useTenantSettings();

  // `status` is optional server-side (all 5 `create*` domain functions
  // default it to `'active'` — see `masterDataConfig.ts`'s `STATUS_FIELD`
  // comment) but the form's own `select` has no blank/neutral option once
  // `required` was dropped from it; seed the same default the backend would
  // apply rather than posting an empty string for a create.
  const [values, setValues] = useState<Record<string, string>>(() => {
    if (isEdit || !config) return {};
    const defaults: Record<string, string> = {};
    for (const field of config.formFields) {
      if (field.key === 'status') defaults[field.key] = 'active';
    }
    if (config.kind === 'party') {
      const kind = defaultPartyKind(settings.salesModel);
      if (kind) defaults['kind'] = kind;
    }
    // A new price list is valid from today unless told otherwise.
    if (config.kind === 'price_list') defaults['effectiveFrom'] = systemClock.today();
    return defaults;
  });
  const [loading, setLoading] = useState(isEdit);
  const [loadError, setLoadError] = useState<'permission_denied' | 'network_error' | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Product catalogue (categories + sale units/prices) travels with the product.
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [unitsDraft, setUnitsDraft] = useState<UnitsDraft>(EMPTY_UNITS_DRAFT);
  // Never send catalogue fields we failed to load: that would wipe them.
  const [catalogReady, setCatalogReady] = useState(!isEdit);
  // Price list lines travel with the list (replaced as a set).
  const [priceLines, setPriceLines] = useState<PriceLineDraft[]>([]);
  const [priceLinesReady, setPriceLinesReady] = useState(!isEdit);
  // The suggested code a create form opened with (null: none could be read).
  const [suggestedCode, setSuggestedCode] = useState<string | null>(null);

  const base = slug ? tenantBasePath(slug) : '';

  // A create form never opens with an empty code: pre-fill the suggested next one.
  useEffect(() => {
    if (isEdit || !slug || !config) return;
    const codeField = config.formFields.find((f) => f.key === 'code');
    if (!codeField || !(codeField.autoCode || codeField.suggestCode)) return;
    let cancelled = false;
    void fetchNextCode(base, config.kind).then((code) => {
      if (cancelled || !code) return;
      setSuggestedCode(code);
      setValues((current) => (current['code'] ? current : { ...current, code }));
    });
    return () => {
      cancelled = true;
    };
  }, [isEdit, slug, config?.kind]);

  useEffect(() => {
    if (isEdit || config?.kind !== 'party') return;
    const kind = defaultPartyKind(settings.salesModel);
    if (!kind) return;
    setValues((current) => (current.kind ? current : { ...current, kind }));
  }, [isEdit, config?.kind, settings.salesModel]);

  useEffect(() => {
    if (!slug || !config || !isEdit) return;
    setLoading(true);
    apiGet<MasterDataRecord>(`${base}/api/v1/master-data/${config.pathSegment}/${id}`)
      .then((record) => {
        const next: Record<string, string> = {};
        for (const field of config.formFields) {
          const raw = record[field.key];
          if (field.kind === 'multi-select') {
            next[field.key] = Array.isArray(raw) ? raw.join(',') : '';
          } else {
            next[field.key] = masterDataFormValue(raw);
          }
        }
        setValues(next);
        if (config.kind === 'price_list') {
          const lines = (
            record as { lines?: { productId: string; uomId: string; unitPriceRial: string }[] }
          ).lines;
          setPriceLines(draftsFromListLines(lines ?? []));
          setPriceLinesReady(Array.isArray(lines));
        }
        if (config.kind === 'product') {
          apiGet<CatalogProduct>(`${base}/api/v1/master-data/product-catalog/${id}`)
            .then((catalog) => {
              setCategoryIds(catalog.categories.map((c) => c.categoryId));
              setUnitsDraft(draftFromCatalogUnits(catalog.units));
              setCatalogReady(true);
            })
            .catch(() => setCatalogReady(false));
        }
      })
      .catch((err: unknown) => {
        setLoadError(
          err instanceof ApiError && err.status === 403 ? 'permission_denied' : 'network_error',
        );
      })
      .finally(() => setLoading(false));
    // config itself is derived from `entity` each render; pathSegment is the
    // stable part of it worth depending on.
  }, [slug, config?.pathSegment, id, isEdit]);

  if (!slug || !config) return null;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload: Record<string, unknown> = { ...values };
      if (config!.kind === 'product' && catalogReady) {
        const draftError = unitsDraftError(unitsDraft);
        if (draftError) {
          setSubmitError(draftError);
          return;
        }
        payload['categoryIds'] = categoryIds;
        if (values['baseUomId']) payload['units'] = unitsPayload(values['baseUomId'], unitsDraft);
      }
      for (const field of config!.formFields) {
        if (field.kind === 'multi-select') {
          payload[field.key] = multiSelectValues(values[field.key]);
        }
      }
      if (config!.kind === 'price_list') {
        // Dates: blank clears the end of validity on edit, and is omitted on create.
        for (const key of ['effectiveFrom', 'effectiveTo']) {
          if (payload[key] === '') {
            if (isEdit && key === 'effectiveTo') payload[key] = null;
            else delete payload[key];
          }
        }
        if (priceLinesReady) {
          const lineProblem = priceLinesError(priceLines);
          if (lineProblem) {
            setSubmitError(lineProblem);
            return;
          }
          payload['lines'] = priceLinesPayload(priceLines);
        }
      }
      if (isEdit) {
        await apiPatch(`${base}/api/v1/master-data/${config!.pathSegment}/${id}`, payload);
      } else {
        await apiPost(`${base}/api/v1/master-data/${config!.pathSegment}`, payload);
      }
      navigate(`${base}/master-data/${config!.pathSegment}`);
    } catch (err) {
      if (!isEdit && err instanceof ApiError && err.status === 409 && suggestedCode !== null) {
        // The suggested code was taken meanwhile: swap in a fresh one instead of dead-ending.
        const fresh = await fetchNextCode(base, config!.kind);
        if (fresh && fresh !== values['code']) {
          setSuggestedCode(fresh);
          setValues((v) => ({ ...v, code: fresh }));
          setSubmitError(
            'این کد قبلاً استفاده شده بود؛ کد جدیدی پیشنهاد شد. بررسی کنید و دوباره ذخیره کنید.',
          );
          return;
        }
      }
      setSubmitError(masterDataSubmitErrorMessage(err, config!));
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <V2Card>
        <V2CardHeader title={`${isEdit ? 'ویرایش' : 'ایجاد'} ${config.label}`} />
        <div style={{ padding: 20 }}>
          <LoadingState />
        </div>
      </V2Card>
    );
  }

  if (loadError) {
    return (
      <V2Card>
        <V2CardHeader title={config.label} />
        <div style={{ padding: 20 }}>
          <StatePanel
            variant={loadError}
            title={loadError === 'permission_denied' ? 'دسترسی ندارید' : 'بارگذاری ناموفق بود'}
          />
        </div>
      </V2Card>
    );
  }

  return (
    <V2Card>
      <V2CardHeader title={`${isEdit ? 'ویرایش' : 'ایجاد'} ${config.label}`} />
      <form
        onSubmit={onSubmit}
        style={{ padding: '0 20px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}
      >
        <V2FormGrid>
          {config.formFields.map((field) => (
            <V2FormField
              key={field.key}
              label={field.label}
              required={fieldRequired(
                field,
                isEdit,
                field.key === 'code' && suggestedCode !== null,
              )}
              hint={
                field.key === 'code' && !isEdit && suggestedCode !== null
                  ? 'کد پیشنهادی سیستم؛ در صورت نیاز تغییر دهید'
                  : field.autoCode && !isEdit
                    ? 'در صورت خالی بودن، به‌صورت خودکار تولید می‌شود'
                    : undefined
              }
              width={field.kind === 'entity-picker' ? 'wide' : 'medium'}
            >
              {field.kind === 'multi-select' ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                  {field.options?.map((opt) => (
                    <label
                      key={opt.value}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                    >
                      <input
                        type="checkbox"
                        className="sk-check"
                        checked={multiSelectValues(values[field.key]).includes(opt.value)}
                        onChange={() =>
                          setValues((v) => ({
                            ...v,
                            [field.key]: toggleMultiSelectValue(v[field.key], opt.value),
                          }))
                        }
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              ) : field.kind === 'entity-picker' && field.pickerEntity ? (
                <EntityPickerField
                  base={base}
                  pickerEntity={field.pickerEntity}
                  value={values[field.key] ?? ''}
                  onChange={(val) => setValues((v) => ({ ...v, [field.key]: val }))}
                  required={fieldRequired(field, isEdit)}
                />
              ) : field.kind === 'date' ? (
                <JalaliDateInput
                  value={values[field.key] ?? ''}
                  onChange={(val) => setValues((v) => ({ ...v, [field.key]: val }))}
                />
              ) : field.kind === 'select' ? (
                <select
                  className="sk-select"
                  value={values[field.key] ?? ''}
                  required={fieldRequired(
                    field,
                    isEdit,
                    field.key === 'code' && suggestedCode !== null,
                  )}
                  onChange={(e) => setValues((v) => ({ ...v, [field.key]: e.target.value }))}
                >
                  <option value="" disabled>
                    انتخاب {field.label}
                  </option>
                  {field.options?.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className={field.kind === 'number' ? 'sk-input ltr' : 'sk-input'}
                  type={field.kind === 'number' ? 'number' : 'text'}
                  value={values[field.key] ?? ''}
                  required={fieldRequired(field, isEdit)}
                  onChange={(e) => setValues((v) => ({ ...v, [field.key]: e.target.value }))}
                />
              )}
            </V2FormField>
          ))}
        </V2FormGrid>
        {config.kind === 'product' ? (
          <>
            <V2FormField
              label="دسته‌بندی"
              width="wide"
              hint="چند دسته‌بندی قابل انتخاب است؛ دسته‌ای که نیست را همین‌جا بسازید."
            >
              <ProductCategoryField base={base} value={categoryIds} onChange={setCategoryIds} />
            </V2FormField>
            <V2FormField
              label="واحدها و قیمت فروش"
              width="wide"
              hint="واحدهای قابل فروش، معادل هر واحد با واحد پایه و قیمت هر واحد."
            >
              <ProductUnitsEditor
                base={base}
                baseUomId={values['baseUomId'] ?? ''}
                value={unitsDraft}
                onChange={setUnitsDraft}
              />
            </V2FormField>
            {isEdit && !catalogReady ? (
              <Alert tone="warning" title="دسته‌بندی و واحدها بارگذاری نشد">
                تغییر این بخش‌ها در این ذخیره اعمال نمی‌شود؛ صفحه را دوباره باز کنید.
              </Alert>
            ) : null}
          </>
        ) : null}
        {config.kind === 'price_list' ? (
          <V2FormField
            label="اقلام لیست قیمت"
            width="wide"
            hint="قیمت هر کالا به تفکیک واحد؛ فقط واحدهای فروش همان کالا قابل انتخاب است."
          >
            <PriceListLinesEditor base={base} value={priceLines} onChange={setPriceLines} />
          </V2FormField>
        ) : null}
        {isEdit && config.kind === 'price_list' && !priceLinesReady ? (
          <Alert tone="warning" title="اقلام لیست بارگذاری نشد">
            تغییر اقلام در این ذخیره اعمال نمی‌شود؛ صفحه را دوباره باز کنید.
          </Alert>
        ) : null}
        {submitError ? (
          <Alert tone="danger" title="خطا">
            {submitError}
          </Alert>
        ) : null}
        <V2FormActions>
          <V2Button
            variant="ghost"
            type="button"
            onClick={() => navigate(`${base}/master-data/${config!.pathSegment}`)}
          >
            بازگشت
          </V2Button>
          <V2Button type="submit" variant="primary" disabled={submitting}>
            {submitting ? 'در حال ذخیره…' : 'ذخیره'}
          </V2Button>
        </V2FormActions>
      </form>
    </V2Card>
  );
}
